import { useState, useEffect } from 'react'
import {
  Car,
  Plus,
  Search,
  RefreshCw,
  Eye,
  Trash2,
  CheckCircle,
  XCircle
} from 'lucide-react'
import { getVehicles, lookupPlate } from '../lib/api'

interface Vehicle {
  id: number
  tenant_id: number
  make: string | null
  model: string | null
  year: number | null
  color: string | null
  license_plate: string
  plate_state: string | null
  lpr_last_seen: string | null
  lpr_match_count: number
  display_name: string
  plate_full: string
  created_at: string
}

export default function Vehicles() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [lookupResult, setLookupResult] = useState<Record<string, unknown> | null>(null)
  const [lookupLoading, setLookupLoading] = useState(false)

  const fetchVehicles = async () => {
    setLoading(true)
    try {
      const data = await getVehicles({ search: search || undefined }) as { vehicles: Vehicle[] }
      setVehicles(data.vehicles || [])
    } catch (err) {
      console.error('Failed to load vehicles:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchVehicles()
  }, [search])

  const handlePlateLookup = async () => {
    if (!search.trim()) return
    setLookupLoading(true)
    try {
      const result = await lookupPlate(search) as Record<string, unknown>
      setLookupResult(result)
    } catch (err) {
      console.error('Lookup failed:', err)
    } finally {
      setLookupLoading(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vehicles</h1>
          <p className="text-gray-600 mt-1">Registered vehicles for LPR tracking</p>
        </div>
        <button
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Vehicle
        </button>
      </div>

      {/* Search & Lookup */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search or enter plate to lookup..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <button
          onClick={handlePlateLookup}
          disabled={lookupLoading || !search.trim()}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
        >
          {lookupLoading ? 'Looking up...' : 'Lookup Plate'}
        </button>
      </div>

      {/* Lookup Result */}
      {lookupResult && (
        <div className={`mb-6 p-4 rounded-lg border ${
          lookupResult.is_match
            ? 'bg-green-50 border-green-200'
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center gap-3">
            {lookupResult.is_match ? (
              <CheckCircle className="w-6 h-6 text-green-500" />
            ) : (
              <XCircle className="w-6 h-6 text-yellow-500" />
            )}
            <div>
              <p className="font-medium text-gray-900">
                Plate: {String(lookupResult.plate)}
              </p>
              {lookupResult.is_match ? (
                <p className="text-sm text-gray-600">
                  Registered to: {String(lookupResult.tenant_name)} | 
                  Building: {String(lookupResult.building_name)} | 
                  Unit: {String(lookupResult.unit_number)}
                </p>
              ) : (
                <p className="text-sm text-gray-600">
                  No matching vehicle found in system
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setLookupResult(null)}
            className="mt-2 text-sm text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        </div>
      )}

      {/* Vehicles Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Car className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No vehicles found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vehicle
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    License Plate
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    LPR Matches
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Seen
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {vehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Car className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {vehicle.display_name}
                          </p>
                          {vehicle.color && (
                            <p className="text-sm text-gray-500">{vehicle.color}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                        {vehicle.plate_full}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-medium ${
                        vehicle.lpr_match_count > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {vehicle.lpr_match_count} matches
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {vehicle.lpr_last_seen
                        ? new Date(vehicle.lpr_last_seen).toLocaleString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button className="p-1 text-gray-400 hover:text-primary-600">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-1 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

